import { Feather, FontAwesome5, MaterialIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState, useEffect, useMemo } from "react";
import {
  Dimensions,
  View,
  Text,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Animated
} from "react-native";
import Collapsible from "react-native-collapsible";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  StepHeader,
  BackButton,
  FloatingButton,
  Loading,
  Centered,
} from "../../../components/all";
import { updateProfile } from "../../../services/profile";
import { useMutation } from "@tanstack/react-query";
import { useFacilityData } from "../../../hooks/useFacilityData";
import { addFields } from "../../../reducers/profile/profile";
import Styles from "../../../constants/styles";
import colors from "../../../constants/colors";
import { extractFacilityTitleAndSubtitle } from "../../../utils/facilityUtils";

const EditFacility = (props) => {
  const safeInsets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const { fields } = useSelector((state) => state.profile);
  const { country } = useSelector((state) => state.user);

  const [verifying, setVerifying] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [expandedTypes, setExpandedTypes] = useState([]);
  const [animatedValues, setAnimatedValues] = useState({});
  const [arrowAnimations, setArrowAnimations] = useState({});

  // Icon mapping for facility types
  const facilityIcons = {
    'hospital': { icon: 'local-hospital', color: '#4B9BFF', bgColor: '#E3F2FF' },
    'clinic': { icon: 'medical-services', color: '#10B981', bgColor: '#D1FAE5' },
    'pharmacy': { icon: 'medication', color: '#F59E0B', bgColor: '#FEF3C7' },
    'lab': { icon: 'biotech', color: '#8B5CF6', bgColor: '#EDE9FE' },
    'nursing': { icon: 'healing', color: '#EC4899', bgColor: '#FCE7F3' },
    'rehab': { icon: 'accessible', color: '#06B6D4', bgColor: '#E0F2FE' },
    'default': { icon: 'health-and-safety', color: colors.primary, bgColor: colors.primary + '10' }
  };

  // Use new facility data hook with fallback to old system
  const {
    data: facilityData,
    isLoading,
    error,
    refetch,
    isUsingNewSystem
  } = useFacilityData(country || 'MY');

  const facilities = facilityData || [];

  // Get icon for facility based on name
  const getFacilityIcon = (facilityName) => {
    const lowerName = facilityName.toLowerCase();
    if (lowerName.includes('hospital')) return facilityIcons.hospital;
    if (lowerName.includes('clinic')) return facilityIcons.clinic;
    if (lowerName.includes('pharmacy')) return facilityIcons.pharmacy;
    if (lowerName.includes('lab')) return facilityIcons.lab;
    if (lowerName.includes('nursing')) return facilityIcons.nursing;
    if (lowerName.includes('rehab')) return facilityIcons.rehab;
    return facilityIcons.default;
  };

  // Pre-select current facility if it exists (only on initial load)
  useEffect(() => {
    if (!fields?.fac_id || !facilityData) {
      return;
    }

    // Only run this once when facilityData loads, don't re-run on selectedFacility changes
    if (selectedFacility !== null) {
      return;
    }

    let matchedFacility = null;
    let parentToExpand = null;

    for (const facility of facilityData) {
      if (facility.fac_id === fields.fac_id) {
        matchedFacility = facility;
        break;
      }

      if (facility.types && facility.types.length > 0) {
        const childFacility = facility.types.find(
          (type) => type.fac_id === fields.fac_id
        );

        if (childFacility) {
          matchedFacility = childFacility;
          parentToExpand = facility.id;
          break;
        }
      }
    }

    if (matchedFacility) {
      setSelectedFacility(matchedFacility);

      if (parentToExpand) {
        setExpandedTypes((prev) =>
          prev.includes(parentToExpand) ? prev : [...prev, parentToExpand]
        );
      }
    }
  }, [facilityData, fields?.fac_id]);

  const toggleExpanded = (facilityId) => {
    setExpandedTypes(prev => {
      const isExpanding = !prev.includes(facilityId);

      // Animate arrow rotation
      if (arrowAnimations[facilityId]) {
        Animated.spring(arrowAnimations[facilityId], {
          toValue: isExpanding ? 1 : 0,
          useNativeDriver: true,
          tension: 50,
          friction: 7,
        }).start();
      }

      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      }
      return [...prev, facilityId];
    });
  };

  const selectFacility = (item) => {
    setSelectedFacility(item);
  };

  const filteredFacilities = useMemo(() => {
    if (!searchText) return facilities;

    const lowerSearch = searchText.toLowerCase();
    return facilities.filter((facility) => {
      const matchesName = facility.name.toLowerCase().includes(lowerSearch);

      // Check if any types match the search
      const hasMatchingType = facility.types?.some(type =>
        type.name.toLowerCase().includes(lowerSearch)
      );

      return matchesName || hasMatchingType;
    });
  }, [facilities, searchText]);

  // Initialize animations for each facility
  useEffect(() => {
    const newAnimatedValues = {};
    const newArrowAnimations = {};

    filteredFacilities.forEach((facility, index) => {
      if (!animatedValues[facility.id]) {
        newAnimatedValues[facility.id] = new Animated.Value(0);
        newArrowAnimations[facility.id] = new Animated.Value(0);
      }
    });

    if (Object.keys(newAnimatedValues).length > 0) {
      setAnimatedValues(prev => ({ ...prev, ...newAnimatedValues }));
      setArrowAnimations(prev => ({ ...prev, ...newArrowAnimations }));
    }
  }, [filteredFacilities]);

  // Animate entrance when data loads
  useEffect(() => {
    if (!isLoading && Object.keys(animatedValues).length > 0) {
      const animations = Object.entries(animatedValues).map(([id, animValue], index) => {
        return Animated.timing(animValue, {
          toValue: 1,
          duration: 400,
          delay: index * 100,
          useNativeDriver: true,
        });
      });

      Animated.parallel(animations).start();
    }
  }, [isLoading, animatedValues]);

  const onUpdateFacilities = useMutation(updateProfile, {
    onError: (error) => {
      setVerifying(false);
      alert(
        "Update Error: " +
        (error.response?.data?.message || "Failed to update facility. Please try again.")
      );
    },
    onSuccess: (res) => {
      setVerifying(false);
      // Update Redux state with new facility
      dispatch(addFields({
        fac_id: selectedFacility?.fac_id,
        facility: selectedFacility,
      }));

      props.navigation.navigate({
        name: "ProfileScreen",
        params: {
          profileEditSuccess: Math.random(),
        },
        merge: true,
      });
    },
  });

  const onPressUpdate = () => {
    if (!selectedFacility) {
      alert("Please select a facility");
      return;
    }

    setVerifying(true);

    // Prepare update data similar to EditProfession
    const updateData = {
      fac_id: selectedFacility.fac_id,
      scope: "clinic_owner"
    };

    onUpdateFacilities.mutate(updateData);
  };

  // Show loading state
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'white' }}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={{ marginTop: 16 }}>Loading facility types...</Text>
      </View>
    );
  }

  // Show error state
  if (error) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32, backgroundColor: 'white' }}>
        <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 16 }}>
          Unable to load facility types. Please check your internet connection and try again.
        </Text>
        <TouchableOpacity
          onPress={() => refetch()}
          style={{
            backgroundColor: colors.primary,
            paddingHorizontal: 24,
            paddingVertical: 12,
            borderRadius: 8,
          }}
        >
          <Text style={{ color: 'white', fontWeight: 'bold' }}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={{
          ...Styles.webSmallContainer,
          backgroundColor: "white",
        }}
      >
        <BackButton />
        <StepHeader
          label={`Edit Facility`}
          icon={<MaterialIcons name="business" size={24} color="black" />}
          subtitle={`Select your medical facility type`}
        />
        <View style={{
          marginHorizontal: 16,
          marginTop: 32,
        }}>
          <Input
            placeholder="Search Facility Type"
            onChangeText={(text) => {
              setSearchText(text);
              // Auto-expand when searching
              if (text.length > 0) {
                setExpandedTypes(filteredFacilities.map(f => f.id));
              } else {
                setExpandedTypes([]);
              }
            }}
          />
        </View>
        <View
          style={{
            width: Dimensions.get("window").width,
            paddingHorizontal: 16,
            marginTop: 16,
          }}
        >
          {filteredFacilities.map((facility, index) => {
            const isExpanded = expandedTypes.includes(facility.id) || searchText.length > 0;
            const iconConfig = getFacilityIcon(facility.name);
            const animValue = animatedValues[facility.id] || new Animated.Value(1);
            const arrowAnim = arrowAnimations[facility.id] || new Animated.Value(0);
            const hasSubtypes = facility.types && facility.types.length > 0;

            return (
              <Animated.View
                key={facility.id}
                style={{
                  marginBottom: 16,
                  opacity: animValue,
                  transform: [
                    {
                      translateY: animValue.interpolate({
                        inputRange: [0, 1],
                        outputRange: [20, 0],
                      }),
                    },
                  ],
                }}
              >
                {/* Parent Facility */}
                <TouchableOpacity
                  onPress={() => {
                    if (hasSubtypes) {
                      toggleExpanded(facility.id);
                    } else {
                      selectFacility(facility);
                    }
                  }}
                  activeOpacity={0.7}
                  style={{
                    backgroundColor: isExpanded ? iconConfig.bgColor : "white",
                    paddingHorizontal: 20,
                    paddingVertical: 18,
                    borderRadius: 20,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderWidth: 2,
                    borderColor: (!hasSubtypes && selectedFacility?.id === facility.id) ? iconConfig.color : (isExpanded ? iconConfig.color : "#F3F4F6"),
                    shadowColor: "#000",
                    shadowOffset: {
                      width: 0,
                      height: 4,
                    },
                    shadowOpacity: isExpanded ? 0.15 : 0.08,
                    shadowRadius: 12,
                    elevation: 3,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <View
                      style={{
                        width: 48,
                        height: 48,
                        backgroundColor: (isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? iconConfig.color : iconConfig.bgColor,
                        borderRadius: 24,
                        marginRight: 16,
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <MaterialIcons
                        name={iconConfig.icon}
                        size={24}
                        color={(isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? "white" : iconConfig.color}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      {(() => {
                        const { title, subtitle } = extractFacilityTitleAndSubtitle(facility.name);
                        return (
                          <>
                            <Text style={{
                              fontSize: 18,
                              fontWeight: "700",
                              color: (isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? iconConfig.color : "#1F2937",
                              letterSpacing: -0.5,
                            }}>
                              {title}
                            </Text>
                            {subtitle && (
                              <Text style={{
                                fontSize: 14,
                                fontWeight: "500",
                                color: (isExpanded || (!hasSubtypes && selectedFacility?.id === facility.id)) ? iconConfig.color + 'CC' : "#6B7280",
                                letterSpacing: -0.2,
                                marginTop: 2,
                              }}>
                                {subtitle}
                              </Text>
                            )}
                          </>
                        );
                      })()}
                      {hasSubtypes && (
                        <Text style={{
                          fontSize: 13,
                          color: isExpanded ? "#4B5563" : "#6B7280",
                          marginTop: 4,
                          letterSpacing: -0.1,
                        }}>
                          {facility.types.length} type{facility.types.length !== 1 ? 's' : ''}
                        </Text>
                      )}
                    </View>
                  </View>
                  {hasSubtypes && (
                    <Animated.View
                      style={{
                        transform: [
                          {
                            rotate: arrowAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: ['0deg', '90deg'],
                            }),
                          },
                        ],
                      }}
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={24}
                        color={isExpanded ? iconConfig.color : "#9CA3AF"}
                      />
                    </Animated.View>
                  )}
                  {!hasSubtypes && selectedFacility?.id === facility.id && (
                    <MaterialIcons
                      name="check-circle"
                      size={24}
                      color={iconConfig.color}
                    />
                  )}
                </TouchableOpacity>

                {/* Subtypes */}
                {hasSubtypes && (
                  <Collapsible collapsed={!isExpanded}>
                    <View style={{ marginTop: 12, marginLeft: 0 }}>
                      {facility.types.map((subtype, subtypeIndex) => {
                        const isSelected = selectedFacility?.id === subtype.id;
                        return (
                          <Animated.View
                            key={subtype.id}
                            style={{
                              opacity: isExpanded ? 1 : 0,
                              transform: [
                                {
                                  translateX: isExpanded ? 0 : -20,
                                },
                                {
                                  scale: isSelected ? 0.98 : 1,
                                },
                              ],
                            }}
                          >
                            <TouchableOpacity
                              onPress={() => selectFacility(subtype)}
                              activeOpacity={0.7}
                              style={{
                                backgroundColor: isSelected
                                  ? iconConfig.color + '15'
                                  : "white",
                                paddingHorizontal: 20,
                                paddingVertical: 16,
                                marginBottom: 8,
                                borderRadius: 16,
                                borderWidth: 2,
                                borderColor: isSelected
                                  ? iconConfig.color
                                  : "#E5E7EB",
                                flexDirection: 'row',
                                alignItems: 'center',
                                shadowColor: "#000",
                                shadowOffset: {
                                  width: 0,
                                  height: 2,
                                },
                                shadowOpacity: isSelected ? 0.1 : 0.05,
                                shadowRadius: 6,
                                elevation: isSelected ? 2 : 1,
                              }}
                            >
                              <View
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 12,
                                  borderWidth: 2,
                                  borderColor: isSelected ? iconConfig.color : "#D1D5DB",
                                  backgroundColor: isSelected ? iconConfig.color : "white",
                                  marginRight: 12,
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                {isSelected && (
                                  <MaterialIcons
                                    name="check"
                                    size={16}
                                    color="white"
                                  />
                                )}
                              </View>
                              {(() => {
                                const { title: subtypeTitle, subtitle: subtypeSubtitle } = extractFacilityTitleAndSubtitle(subtype.name);
                                return (
                                  <View style={{ flex: 1 }}>
                                    <Text style={{
                                      fontSize: 15,
                                      color: isSelected
                                        ? iconConfig.color
                                        : "#374151",
                                      fontWeight: isSelected
                                        ? "600"
                                        : "400",
                                      letterSpacing: -0.2,
                                    }}>
                                      {subtypeTitle}
                                    </Text>
                                    {subtypeSubtitle && (
                                      <Text style={{
                                        fontSize: 12,
                                        color: isSelected
                                          ? iconConfig.color + 'CC'
                                          : "#6B7280",
                                        fontWeight: "400",
                                        letterSpacing: -0.1,
                                        marginTop: 2,
                                      }}>
                                        {subtypeSubtitle}
                                      </Text>
                                    )}
                                  </View>
                                );
                              })()}
                            </TouchableOpacity>
                          </Animated.View>
                        );
                      })}
                    </View>
                  </Collapsible>
                )}
              </Animated.View>
            );
          })}
        </View>
        <View style={{ marginBottom: 140 }} />
      </ScrollView>

      <FloatingButton status={"rejected"} onPress={() => onPressUpdate()}>
        <View
          style={{
            borderRadius: 100,
            overflow: "hidden",
          }}
        >
          <LinearGradient
            start={{ x: -0.2, y: 0.2 }}
            colors={[
              "rgb(248, 100, 108)",
              "rgb(242, 49, 123)",
              "rgb(234,0,137)",
            ]}
          >
            <View
              style={{
                height: 70,
                width: 70,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Feather name={"check-circle"} size={30} color="white" />
            </View>
          </LinearGradient>
        </View>
      </FloatingButton>
      {Platform.OS !== "web" ? (
        <Loading visible={verifying} />
      ) : null}
    </>
  );
};

const styles = {
  category: {
    height: 64,
    justifyContent: "center",
    margin: 6,
    marginTop: 10,
    paddingHorizontal: 24,
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 18,
    width: "80%",
  },
};

export default EditFacility;
